import { Selection, EnterElement, ScaleTime, D3DragEvent } from "d3"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { MarginedBoundingBox } from "../../../../Types/MarginedBoundingBox"
import { ReactCallbacks } from "../../../../Types/ReactCallbacks"
import { InProgressAnnotation } from "../../../../Types/Annotations"
import { MobergFontFamily } from "../../../../../../../../Moberg"
import { TimeSpan } from "../../../../Data/EEGViewRegistry"

export type D3GraphsOverlayConfig = {
	boundingBox: MarginedBoundingBox
	svg: SVGSVGElement | null
	svgOffsetX: number
    viewScale: ScaleTime<any, any, any>
	clipPathId: string
	inProgressAnnotation: InProgressAnnotation
	liveModeEnabled: boolean
	// A cursor line that stays put (Time Series windows): a click places it, its time shows at
	// the top right, and the numbers on the right are read at it. Until a time is picked it rests
	// at the right edge, where the latest values are read. Other windows keep the line that
	// follows the mouse.
	pinnedCursor?: boolean
	// The picked time, owned by the window's controller (null: none picked).
	getCursorTime?: () => number | null
	timeZone?: string
	onCursorChange?: (time: number | null) => void
	// With the pinned cursor, a click also centers the EEG windows on its time, and a bar above
	// the graphs marks the time each EEG window shows.
	onJump?: (time: number) => void
	getEEGViews?: () => TimeSpan[]
	// An EEG window's marker for the time picked in a trend window, or null when there is none.
	getMarkedTime?: () => number | null
}

// Theme colours, so light and dark mode both read well.
const CURSOR_COLOR = "var(--color-value-cursor)"
const LINK_COLOR = "var(--color-accent-interactive)"

// One formatter per time zone: building one on every frame (panning, live mode) is wasteful.
const timeFormatters = new Map<string, Intl.DateTimeFormat>()
const formatterFor = (timeZone: string | undefined) => {
	const key = timeZone ?? ""
	let formatter = timeFormatters.get(key)
	if (!formatter) {
		// 12 hour clock with AM/PM, like the time axis under it.
		formatter = new Intl.DateTimeFormat("en-US", { timeZone: timeZone || undefined, month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true })
		timeFormatters.set(key, formatter)
	}
	return formatter
}
const EEG_BAR_HEIGHT = 5
const EEG_BAR_MIN_WIDTH = 5
const EEG_BAR_COLOR = "var(--color-chart-axis-text, #555)"
// The bar's color, mostly transparent, so the wash reads as part of the bar on heatmaps and line graphs alike.
const EEG_SHADE_OPACITY = 0.15

export class D3GraphsOverlay extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3GraphsOverlayConfig, ReactCallbacks<any>> {
	private rectOverlayClassName: string = "d3-rect-overlay"
	private hoverlineClassName: string = "d3-hoverline"
	private inProgressAnnotationClassName: string = "d3-in-progress-annotation"
	// The label's last text and room, so frames that change nothing skip re-measuring it.
	private lastLabelKey = ""
	private lastHoveredX: number = 0
	private isWindowActive = false
	private hoverLineSelection: Selection<any, any, any, any> | undefined = undefined
	private inProgressAnnotationSelection: Selection<any, any, any, any> | undefined = undefined
	private overlaySelection: Selection<any, any, any, any> | undefined = undefined
	private cursorLineSelection: Selection<any, any, any, any> | undefined = undefined
	private cursorLabelSelection: Selection<any, any, any, any> | undefined = undefined
	private eegViewsSelection: Selection<any, any, any, any> | undefined = undefined
	private markedTimeSelection: Selection<any, any, any, any> | undefined = undefined

	constructor(root: SVGGElement, config: D3GraphsOverlayConfig, reactCallbacks: ReactCallbacks<any>) {
		super(root, config, "d3-hover-overlay-group", reactCallbacks)
		this.render()
	}

	public getLastHoveredDate = () => {
		return this.config.viewScale.invert(this.lastHoveredX)
	}

	// The placed time while it is on screen; null means the right edge.
	public getCursorTime = (): number | null => (this.config.pinnedCursor ? this.config.getCursorTime?.() ?? null : null)

	// The marked time, the EEG's time or the view changed: redraw the line, its time, the EEG bar
	// and the EEG marker now, without a full render.
	public cursorChanged = () => {
		this.updateCursor()
		this.updateEEGViews()
		this.updateMarkedTime()
	}

	onClick = (event: MouseEvent) => {
		// A click on a graph's name hides that trace and does not move the line. Names are HTML
		// inside the drawing (or a legend label); the data itself is drawn on canvases and SVG.
		const target = event.target as Element | null
		const onName = !!target && ((target instanceof HTMLElement && !(target instanceof HTMLCanvasElement)) || !!target.closest("[class*='legend-label']"))
		if (!this.config.pinnedCursor || event.defaultPrevented || onName) {
			return
		}
		const rect = this.hitBox()
		if (!rect || event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
			return
		}
		const time = this.config.viewScale.invert(this.getMouseXFromMouseEvent(event)).getTime()
		this.config.onCursorChange?.(time)
		this.config.onJump?.(time)
	}

	// A bar above the graphs for each EEG window's time, in the spot event review marks the current
	// event, and a light wash below it over the graphs. At least a few pixels wide so a short EEG page
	// stays visible on hours of trends; hidden when the EEG's time is off screen.
	private updateEEGViews() {
		const spans = this.config.pinnedCursor ? this.config.getEEGViews?.() ?? [] : []
		const { width } = this.config.boundingBox
		const bars = spans
			.map(span => ({ start: this.config.viewScale(span.start), end: this.config.viewScale(span.end) }))
			.filter(({ start, end }) => end >= 0 && start <= width)
			.map(({ start, end }) => {
				const middle = (start + end) / 2
				const barWidth = Math.max(EEG_BAR_MIN_WIDTH, end - start)
				const x = Math.max(0, middle - barWidth / 2)
				return { x, width: Math.min(width, middle + barWidth / 2) - x }
			})
			.filter(bar => bar.width > 0)

		this.eegViewsSelection
			?.selectAll<SVGRectElement, { x: number, width: number }>(".d3-eeg-view-shade")
			.data(bars)
			.join("rect")
			.attr("class", "d3-eeg-view-shade")
			.attr("x", bar => bar.x)
			.attr("y", 0)
			.attr("width", bar => bar.width)
			.attr("height", this.config.boundingBox.height)
			.style("fill", EEG_BAR_COLOR)
			.attr("fill-opacity", EEG_SHADE_OPACITY)
			.attr("pointer-events", "none")

		this.eegViewsSelection
			?.selectAll<SVGRectElement, { x: number, width: number }>(".d3-eeg-view-bar")
			.data(bars)
			.join("rect")
			.attr("class", "d3-eeg-view-bar")
			.attr("x", bar => bar.x)
			.attr("y", -this.config.boundingBox.y)
			.attr("width", bar => bar.width)
			.attr("height", EEG_BAR_HEIGHT)
			.attr("rx", 1)
			.style("fill", EEG_BAR_COLOR)
			.attr("pointer-events", "none")
	}

	private updateMarkedTime() {
		const time = this.config.getMarkedTime?.() ?? null
		const x = time === null ? 0 : this.config.viewScale(time)
		this.markedTimeSelection
			?.attr("x1", x)
			.attr("x2", x)
			.attr("y2", this.config.boundingBox.height)
			.attr("display", time === null ? "none" : null)
	}

	// The area that counts as "on the graphs". With a pinned cursor the overlay group also holds
	// its time label (top right, over the numbers), so the group's box would let a click on the
	// label or the numbers pick a time past the right edge: use the graphs' own rectangle. Other
	// windows keep the group's box, as before.
	private hitBox(): DOMRect | undefined {
		const node = this.config.pinnedCursor
			? this.overlaySelection?.select<SVGRectElement>("." + this.rectOverlayClassName).node()
			: this.overlaySelection?.node()
		return node?.getBoundingClientRect()
	}

	private formatTime(time: number) {
		return formatterFor(this.config.timeZone).format(new Date(time))
	}

	private updateCursor() {
		if (!this.config.pinnedCursor) {
			return
		}
		const placed = this.getCursorTime()
		const time = placed ?? this.config.viewScale.domain()[1].getTime()
		const width = this.config.boundingBox.width
		const rawX = this.config.viewScale(time)
		const x = Math.max(0, Math.min(width - 1, rawX))
		// A picked time scrolled off screen keeps its numbers and its time at the top right; only
		// the line goes, rather than sitting at the edge as if it marked another time.
		const onScreen = placed === null || (rawX >= 0 && rawX <= width)

		this.cursorLineSelection
			?.attr("x1", x)
			.attr("x2", x)
			.attr("y2", this.config.boundingBox.height)
			.attr("display", onScreen ? null : "none")

		// The time sits at the top right, over the numbers it belongs to (red once a time is
		// picked, like the line). Under it: "Show latest values" as a link once a time is
		// picked, otherwise a grey hint. Both shrink to fit a narrow column.
		const labelX = width + 10
		this.cursorLabelSelection?.attr("transform", `translate(${labelX}, -25)`)
		const timeString = this.formatTime(time)
		const actionString = placed === null ? "Click a graph to pick a time" : "Show latest values"
		const svgWidth = this.config.svg?.getBoundingClientRect().width ?? 0
		const room = svgWidth - this.config.svgOffsetX - this.config.boundingBox.x - labelX - 4
		const labelKey = `${timeString}|${actionString}|${Math.round(room)}`
		if (labelKey === this.lastLabelKey) {
			return
		}
		this.lastLabelKey = labelKey

		const timeText = this.cursorLabelSelection?.select<SVGTextElement>(".d3-cursor-time")
			.text(timeString)
			.style("fill", placed === null ? "var(--text-color)" : CURSOR_COLOR)
		const action = this.cursorLabelSelection?.select<SVGTextElement>(".d3-cursor-action")
			.text(actionString)
			.style("fill", placed === null ? "var(--text-secondary-color)" : LINK_COLOR)
			.style("text-decoration", placed === null ? "none" : "underline")
			.style("cursor", placed === null ? "default" : "pointer")
			.attr("pointer-events", placed === null ? "none" : null)
			.attr("tabindex", placed === null ? null : 0)
			.attr("role", placed === null ? null : "button")
		const fit = (text: typeof timeText, start: number) => {
			let size = start
			text?.attr("font-size", size)
			while (room > 0 && size > 9 && (text?.node()?.getComputedTextLength() ?? 0) > room) {
				size -= 0.5
				text?.attr("font-size", size)
			}
		}
		fit(timeText, 13)
		fit(action, 12)
	}

	enter = (newOverlays: Selection<EnterElement, any, any, any>) => {
		const overlay = newOverlays
			.append("g")
			.attr("class", this.className)
			.attr("transform", `translate(${this.config.boundingBox.x}, ${this.config.boundingBox.y})`)

		overlay
			.append("rect")
			.attr("class", this.rectOverlayClassName)
			.attr("width", this.config.boundingBox.width)
			.attr("height", this.config.boundingBox.height)
			.attr("fill", "transparent")
			.style("cursor", this.config.liveModeEnabled ? "default" : "hand")
			.attr("pointer-events", "none")

		this.overlaySelection = overlay

		this.eegViewsSelection = overlay.append("g").attr("class", "d3-eeg-views")

		this.markedTimeSelection = overlay
			.append("line")
			.attr("class", "d3-marked-time")
			.style("stroke", CURSOR_COLOR)
			.attr("stroke-width", 1.5)
			.attr("pointer-events", "none")

		this.hoverLineSelection = overlay
			.append("line")
			.attr("class", this.hoverlineClassName)
			.attr("opacity", 0)
			.attr("y2", this.config.boundingBox.height)
			.attr("stroke", this.config.inProgressAnnotation.color)
			.attr("pointer-events", "none")

		this.inProgressAnnotationSelection = overlay
			.append("rect")
			.attr("clip-path", `url(#${this.config.clipPathId})`)
			.attr("class", this.inProgressAnnotationClassName)
			.attr("height", this.config.boundingBox.height)
			.attr("fill", this.config.inProgressAnnotation.color)
			.attr("pointer-events", "none")

		this.syncCursorElements(overlay)
		this.updateCursor()
		this.updateEEGViews()
		this.updateMarkedTime()

		return overlay
	}

	// The cursor line and its time exist only while the window has the setting on; turning
	// it off (Configure Graphs, Window Settings) removes them and brings back the hover line.
	private syncCursorElements(overlay: Selection<any, any, any, any>) {
		if (!this.config.pinnedCursor) {
			this.cursorLineSelection?.remove()
			this.cursorLabelSelection?.remove()
			this.cursorLineSelection = undefined
			this.cursorLabelSelection = undefined
			this.lastLabelKey = ""
			return
		}
		if (this.cursorLineSelection) {
			return
		}

		this.cursorLineSelection = overlay
			.append("line")
			.attr("class", "d3-cursor-line")
			.style("stroke", CURSOR_COLOR) // a style, so the theme variable resolves
			.attr("stroke-width", 1.5)
			.attr("pointer-events", "none")

		this.cursorLabelSelection = overlay.append("g").attr("class", "d3-cursor-label")
		this.cursorLabelSelection
			.append("text")
			.attr("class", "d3-cursor-time")
			.attr("font-family", MobergFontFamily.REGULAR)
			.attr("font-size", 13)
			.attr("font-weight", 600)
			.style("fill", "var(--text-color)")
			.attr("pointer-events", "none")

		// Under the time: a hint before any click, then a link back to the latest values.
		this.cursorLabelSelection
			.append("text")
			.attr("class", "d3-cursor-action")
			.attr("y", 15)
			.attr("font-family", MobergFontFamily.REGULAR)
			.on("mousedown", (event: MouseEvent) => event.stopPropagation())
			.on("click", (event: MouseEvent) => {
				event.stopPropagation()
				this.showLatestValues()
			})
			// Reachable from the keyboard too (it takes focus once a time is picked).
			.on("keydown", (event: KeyboardEvent) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault()
					event.stopPropagation()
					this.showLatestValues()
				}
			})
		this.lastLabelKey = ""
	}

	private showLatestValues() {
		if (this.getCursorTime() !== null) {
			this.config.onCursorChange?.(null)
		}
	}

	update = (updatedOverlays: Selection<any, any, any, any>) => {
		updatedOverlays
			.select("." + this.rectOverlayClassName)
			.attr("width", this.config.boundingBox.width)
			.attr("height", this.config.boundingBox.height)
			.style("cursor", this.config.liveModeEnabled ? "default" : "hand")

		this.overlaySelection = updatedOverlays

		this.hoverLineSelection?.attr("stroke", this.config.inProgressAnnotation.color)

		this.updateInProgressAnnotation(this.lastHoveredX)
		this.hoverlineMoved(this.lastHoveredX)
		this.syncCursorElements(updatedOverlays)
		this.updateCursor()
		this.updateEEGViews()
		this.updateMarkedTime()

		return updatedOverlays
	}

	private hoverlineMoved = (x: number): void => {		
		this.hoverLineSelection
			?.attr("x1", x)
			.attr("x2", x)
            .attr("y2", this.config.boundingBox.height)
	}

	private getMouseXFromMouseEvent(event: MouseEvent) {
		const point = this.transformSVGCoordinateToScreenCoordinate(event)
		return point.x - this.config.svgOffsetX
	}

	onHover = (event: MouseEvent) => {
		if (event.buttons > 0) {
			// The user is clicking or dragging, not really hovering.
			return
		}

		const rect = this.hitBox()
		if (!rect) {
			return
		}

		const isInside = event.clientX >= rect.left && 
			event.clientX <= rect.right && 
			event.clientY >= rect.top && 
			event.clientY <= rect.bottom

		if (!isInside) {
			this.onMouseLeave()
			return
		} 

		this.isWindowActive = true

		const mouseX = this.getMouseXFromMouseEvent(event)
		// With a pinned cursor, the line only follows the mouse while an annotation is drawn.
		if (!this.config.pinnedCursor || this.config.inProgressAnnotation.startDate) {
			this.showHoverline()
		} else {
			this.hideHoverline()
		}
		this.hoverlineMoved(mouseX)
		this.updateInProgressAnnotation(mouseX)
		this.lastHoveredX = mouseX
	}

	onDrag = (event: D3DragEvent<SVGRectElement, any, any>) => {
		const mouseX = this.getMouseXFromMouseEvent(event.sourceEvent)

		// We want to disable panning during live mode because the view should be locked to the end of the display.
		if (this.config.liveModeEnabled) {
			this.hoverlineMoved(mouseX)
			this.lastHoveredX = mouseX
			return
		}

		this.hideHoverline()
		this.hoverlineMoved(mouseX)
	}

	onMouseLeave = () => {
		this.hideHoverline()
		this.hideInProgressAnnotation()
		this.isWindowActive = false
	} 

	private showHoverline = () => {
		this.hoverLineSelection?.attr("opacity", 1)
	}

	private hideHoverline = () => {
		this.hoverLineSelection?.attr("opacity", 0)
	}

	private hideInProgressAnnotation = () => {
		this.inProgressAnnotationSelection?.attr("fill", "transparent")
	}

	private updateInProgressAnnotation(mouseX: number) {
		this.inProgressAnnotationSelection?.select("." + this.inProgressAnnotationClassName)

		if (!this.config.inProgressAnnotation.startDate) {
			this.hideInProgressAnnotation()
			return
		}

		const startDateX = this.config.viewScale(this.config.inProgressAnnotation.startDate.getTime())
		const startX = Math.min(startDateX, mouseX)
		const endX = Math.max(startDateX, mouseX)
		const width = endX - startX

		this.inProgressAnnotationSelection
			?.attr("x", startX)
			.attr("width", width)
			.attr("height", this.config.boundingBox.height)
			.attr("fill", this.config.inProgressAnnotation.color)
			.attr("opacity", this.isWindowActive ? this.config.inProgressAnnotation.opacity : 0)
	}

	private transformSVGCoordinateToScreenCoordinate = (event: MouseEvent) => {
		// https://dev.to/netsi1964/screen-coordinates-to-svg-coordinates-3k0l
		if (this.config.svg === null) {
			return { x: 0, y: 0 }
		}

		const { clientX, clientY } = event
		let point = this.config.svg.createSVGPoint()
		point.x = clientX
		point.y = clientY
		return point.matrixTransform(this.config.svg.getScreenCTM()?.inverse())
	}
}
